import React, { useEffect, useRef } from 'react';
import { projects } from './data';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Projects.scss';

gsap.registerPlugin(ScrollTrigger);

const Projects = () => {
    const projectsRef = useRef(null);
    const titleRef = useRef(null);
    const cardsRef = useRef([]);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Title animation
            gsap.fromTo(titleRef.current,
                {
                    y: 50,
                    opacity: 0
                },
                {
                    scrollTrigger: {
                        trigger: titleRef.current,
                        start: 'top 80%',
                    },
                    y: 0,
                    opacity: 1,
                    duration: 1,
                    ease: 'power3.out'
                }
            );

            // Cards stagger animation
            const cards = document.querySelectorAll('.project-card');
            gsap.fromTo(cards,
                {
                    y: 80,
                    opacity: 0
                },
                {
                    scrollTrigger: {
                        trigger: projectsRef.current,
                        start: 'top 70%',
                    },
                    y: 0,
                    opacity: 1,
                    duration: 0.8,
                    stagger: 0.15,
                    ease: 'power3.out'
                }
            );

        }, projectsRef);

        return () => ctx.revert();
    }, []);

    const getProjectImage = (src) => {
        try {
            return require(`../../assets/projects/${src}`);
        } catch (error) {
            console.error(`Image not found: ${src}`);
            return null;
        }
    };

    return (
        <section className="projects" ref={projectsRef}>
            <div className="projects-container">
                <h2 className="section-title" ref={titleRef}>
                    Featured Projects
                </h2>
                <p className="section-subtitle">
                    AniUI ecosystem, open-source libraries, and selected builds
                </p>

                <div className="projects-grid">
                    {projects.map((project, index) => {
                        const imageSrc = getProjectImage(project.src);
                        const isExternalLink = project.href && !project.href.startsWith('/');
                        const CardWrapper = project.archived ? 'div' : (project.href ? 'a' : 'div');
                        const linkProps = !project.archived && project.href ? {
                            href: project.href,
                            ...(isExternalLink ? { target: "_blank", rel: "noopener noreferrer" } : {})
                        } : {};
                        
                        return (
                            <CardWrapper
                                key={project.id}
                                {...linkProps}
                                className={`project-card ${project.archived ? 'archived' : ''}`}
                                ref={el => cardsRef.current[index] = el}
                            >
                                <div className="project-image-wrapper">
                                    {imageSrc ? (
                                        <img 
                                            src={imageSrc} 
                                            alt={project.name}
                                            className="project-image"
                                        />
                                    ) : (
                                        <div
                                            className="project-image-placeholder"
                                            style={{ background: project.accent || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                                        >
                                            <span className="placeholder-title">{project.name}</span>
                                        </div>
                                    )}
                                    <div className="project-category">
                                        {project.category}
                                    </div>
                                </div>
                                <div className="project-content">
                                    <div className="project-header">
                                        <h3 className="project-title">{project.name}</h3>
                                        {!project.archived && (
                                            <svg className="external-link-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        )}
                                    </div>
                                    <p className="project-description">{project.description}</p>
                                    <div className="project-tech">
                                        {project.tech.map((tech, techIndex) => (
                                            <span key={techIndex} className="tech-badge">
                                                {tech}
                                            </span>
                                        ))}
                                    </div>
                                    {project.archived ? (
                                        <div className="archived-message">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            {project.archivedMessage}
                                        </div>
                                    ) : (
                                        <div className="view-project">
                                            View Project
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            </CardWrapper>
                        );
                    })}
                </div>

                <div className="cta-section">
                    <div className="cta-content">
                        <h3>Have a Project in Mind?</h3>
                        <p>I'm currently available for freelance work and full-time opportunities</p>
                        <a 
                            href="#contact" 
                            className="cta-button"
                            onClick={(e) => {
                                e.preventDefault();
                                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0034 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Get In Touch
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Projects;
